import { useState, useEffect, useMemo } from 'react'

export interface UseSearchAndFilterOptions<T> {
  data: T[]
  searchFields: (keyof T | string)[]
  filterFn?: (item: T, filters: Record<string, any>) => boolean
  onFilterChange?: (filtered: T[]) => void
}

export interface UseSearchAndFilterReturn<T> {
  searchTerm: string
  setSearchTerm: (term: string) => void
  filters: Record<string, any>
  setFilter: (key: string, value: any) => void
  setFilters: (filters: Record<string, any>) => void
  filteredData: T[]
}

/**
 * Custom hook for search and filter logic
 */
export function useSearchAndFilter<T extends Record<string, any>>(
  options: UseSearchAndFilterOptions<T>
): UseSearchAndFilterReturn<T> {
  const { data, searchFields, filterFn, onFilterChange } = options

  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState<Record<string, any>>({})

  const filteredData = useMemo(() => {
    let filtered = data

    // Apply search filter
    if (searchTerm) {
      const lower = searchTerm.toLowerCase()
      filtered = filtered.filter((item) => {
        return searchFields.some((field) => {
          const value = getNestedValue(item, field as string)
          return value?.toString().toLowerCase().includes(lower) || false
        })
      })
    }

    // Apply custom filters
    if (filterFn) {
      filtered = filtered.filter((item) => filterFn(item, filters))
    }

    return filtered
  }, [data, searchTerm, filters, searchFields, filterFn])

  // Callback for external updates (separate effect to avoid dependency issues)
  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(filteredData)
    }
  }, [filteredData, onFilterChange])

  const setFilter = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  return {
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    setFilters,
    filteredData,
  }
}

/**
 * Helper function to get nested object values by path
 */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, prop) => current?.[prop], obj)
}
